import { getOpportunityPropertiesErrors, opportunityPropertiesSchema } from '@opportunities/-lib/opportunityValidation'
import { useOpportunityStatusForm } from '@opportunities/$opportunityId/-hooks/useOpportunityStatusForm'
import { useUpdateOpportunity } from '@opportunities/$opportunityId/-hooks/useUpdateOpportunity'
import type { OpportunityDetail, UpdateOpportunityRequest } from 'backend/types'
import { useMemo, useState } from 'react'
import { OPPORTUNITY_STATUS_ID } from 'shared'
import { toast } from 'sonner'
import { getVisibleErrors, touchAll } from '@/lib/form-validation'

export function usePropertiesTab(opportunity: OpportunityDetail) {
  const [title, setTitle] = useState(opportunity.title)
  const [informationSource, setInformationSource] = useState(opportunity.information_source || '')
  const [description, setDescription] = useState(opportunity.description || '')
  const [isExpansion, setIsExpansion] = useState(opportunity.is_expansion)
  const [startOn, setStartOn] = useState(opportunity.start_on ?? '')
  const [contactName, setContactName] = useState(opportunity.contact_name || '')

  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [focusedField, setFocusedField] = useState<string | null>(null)

  const statusForm = useOpportunityStatusForm({
    statusId: opportunity.opportunity_status_id,
    closeReasonId: opportunity.opportunity_close_reason_id,
    additionalCloseReason: opportunity.opportunity_additional_close_reason || '',
    gCompanyName: opportunity.g_company_name || '',
    gCompanyContact: opportunity.g_company_contact || '',
  })

  const allPropertyErrors = useMemo(
    () => getOpportunityPropertiesErrors({ title, informationSource, description, contactName }),
    [title, informationSource, description, contactName],
  )

  const propertyErrors = getVisibleErrors(allPropertyErrors, touched)

  const errors: Record<string, string> = { ...propertyErrors, ...statusForm.statusErrors }
  const hasErrors = Object.keys(errors).length > 0

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
    setTouched((prev) => ({ ...prev, title: true }))
  }

  const handleInformationSourceChange = (v: string) => {
    setInformationSource(v)
    setTouched((prev) => ({ ...prev, informationSource: true }))
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value)
    setTouched((prev) => ({ ...prev, description: true }))
  }

  const handleContactNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactName(e.target.value)
    setTouched((prev) => ({ ...prev, contactName: true }))
  }

  const { mutate: updateOpportunity, isPending } = useUpdateOpportunity(opportunity.id, {
    onSuccess: () => {
      toast.success('オポチュニティを更新しました')
    },
    onError: (error) => {
      toast.error(`更新に失敗しました: ${error.message}`)
    },
  })

  const handleSubmit = () => {
    // 全フィールドを touched にする
    setTouched(touchAll(opportunityPropertiesSchema))
    statusForm.touchAllStatusFields()

    if (Object.keys(allPropertyErrors).length > 0 || statusForm.hasAllStatusErrors) return

    const { statusState } = statusForm

    const data: UpdateOpportunityRequest = {
      title: title.trim(),
      informationSource: informationSource.trim() || null,
      description: description.trim() || null,
      isExpansion,
      startOn: startOn || null,
      contactName: contactName.trim() || null,
      opportunityStatusId: statusState.statusId,
    }

    if (statusState.closeReasonId) {
      data.opportunityCloseReason = {
        opportunityCloseReasonId: statusState.closeReasonId,
        opportunityAdditionalCloseReason: statusState.additionalCloseReason || null,
      }
    }

    if (statusState.statusId === OPPORTUNITY_STATUS_ID.G_COMPANY_STATUS_ID) {
      data.gCompanyName = statusState.gCompanyName || null
      data.gCompanyContact = statusState.gCompanyContact || null
    }

    updateOpportunity(data)
  }

  return {
    title,
    handleTitleChange,
    informationSource,
    handleInformationSourceChange,
    description,
    handleDescriptionChange,
    isExpansion,
    setIsExpansion,
    startOn,
    setStartOn,
    contactName,
    handleContactNameChange,
    statusState: statusForm.statusState,
    handleStatusChange: statusForm.handleStatusChange,
    handleCloseReasonChange: statusForm.handleCloseReasonChange,
    handleAdditionalCloseReasonChange: statusForm.handleAdditionalCloseReasonChange,
    handleGCompanyNameChange: statusForm.handleGCompanyNameChange,
    handleGCompanyContactChange: statusForm.handleGCompanyContactChange,
    statuses: statusForm.statuses,
    isSystemManaged: statusForm.isSystemManaged,
    groupedCloseReasons: statusForm.groupedCloseReasons,
    isStatusDataLoading: statusForm.isStatusDataLoading,
    currentStatus: statusForm.currentStatus,
    isClosed: statusForm.isClosed,
    isGCompanyFlow: statusForm.isGCompanyFlow,
    isAdditionalCloseReasonRequired: statusForm.isAdditionalCloseReasonRequired,
    errors,
    hasErrors,
    handleSubmit,
    isPending,
    focusedField,
    setFocusedField,
  }
}
